import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { trackFb } from "@/lib/fbq";
import Nav from "@/components/welp/Nav";
import PageBg, { POSTER_P5_REVIEW } from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Camera } from "lucide-react";
import { resizeImageToDataUrl } from "@/lib/resizeImage";

/* ==============================================================================
   NEW REVIEW OPTIONS (2026-07)
   MUST match /app/backend/server.py CHOICE_* exactly. Do not edit one side only.
   ============================================================================== */
const CHOICE_CONNECTION_FEEL = ["Whoa, was that real?", "There could be something here.", "Too early to tell.", "Stay the heck away from me."];
const CHOICE_TIMING = ["Barely... one or two dates.", "Enough to know. Less than 3 months.", "I thought we had something real. Less than a year.", "Bro... this was my person. A year or more."];
const CHOICE_AGE = ["Yes, and I must say, I'm impressed.", "Didn't even question it.", "Hmm, there's definitely some doubt.", "100% Not at all."];
const CHOICE_IDENTITY = ["Even better. What a gem!", "Mostly, we just scratched the surface.", "It's definitely questionable.", "Um... I'm really not sure who that was."];
const CHOICE_APPEARANCE = ["Wow, the pictures did not do justice!", "Cute.", "Could've tried a little.", "That was just rude!"];
const CHOICE_HYGIENE = ["Oh so yummy!", "Not too shabby.", "Didn't even notice.", "Bro, no!"];
const CHOICE_COMM = ["Honestly, I couldn't keep up.", "Nice back and forth.", "Too much silence.", "Like almost nothing at all, pointless!"];
const CHOICE_EXES = ["Not at all, thank goodness.", "Once or twice.", "More than I liked.", "If I hear that name one more time..."];
const CHOICE_ENERGY = ["They bring me life.", "So peaceful.", "A little boring.", "Listen, I can't hang!"];
const CHOICE_TRY_AGAIN = ["I thought you'd never ask!", "Yeah I'd be down if they are.", "I'll have to put some thought into that.", "I respectfully decline."];
const CHOICE_RECOMMEND = ["Um, hello that's mine!", "Yes, that's a great idea.", "I don't see why not.", "Not even to my worst enemy."];
const CHOICE_SAFETY = ["Yes, and I'd like to explain.", "Yes, I'll submit the report.", "Me, scared? Ha!!", "Heck no, it was a blast!!"];

/* --- reusable UI atoms --- */
function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left px-4 py-3 rounded-2xl border-2 transition text-sm font-semibold leading-snug ${active
        ? "bg-[#FF1A82] text-white border-[#FF1A82] shadow-md"
        : "bg-white/70 backdrop-blur-lg text-[#3A3A3A] border-black/10 hover:border-[#FF1A82]/60 hover:bg-[#FF1A82]/10"}`}>
      {children}
    </button>
  );
}

function ChoiceGrid({ options, value, onChange, testidPrefix }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((o, i) => (
        <Pill key={o} active={value === o} onClick={() => onChange(o)} testid={`${testidPrefix}-${i}`}>{o}</Pill>
      ))}
    </div>
  );
}

function ExplainBox({ value, onChange, max = 300, testid, placeholder = "Would you like to explain? (optional)" }) {
  return (
    <div className="mt-3">
      <textarea
        data-testid={testid}
        rows={3}
        maxLength={max}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82] resize-none"
      />
      <div className="text-xs text-[#5A5A5A] font-semibold text-right">{(value || "").length}/{max}</div>
    </div>
  );
}

function TextArea({ label, value, onChange, max = 300, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">{label}</div>
      <textarea
        data-testid={testid}
        rows={4}
        maxLength={max}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-5 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981] resize-none"
      />
      <div className="text-xs text-[#5A5A5A] font-semibold text-right">{(value || "").length}/{max}</div>
    </div>
  );
}

function QSection({ emoji, title, subtitle, children }) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-heading text-2xl font-bold text-[#1A1A1A]">
          <span aria-hidden className="mr-2">{emoji}</span>{title}
        </h3>
        {subtitle && <p className="text-sm text-[#5A5A5A] font-semibold mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/* ============================== */
const emptyAnswer = () => ({ choice: "", explain: "" });

export default function ReviewWizard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(1);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "smooth" }); }, [step]);
  const stepRef = useRef(1);
  const startedRef = useRef(false);
  const doneRef = useRef(false);
  useEffect(() => {
    stepRef.current = step;
    api.evt("review_step", { step });
    if (step > 1 && !startedRef.current) { startedRef.current = true; api.evt("review_started"); }
  }, [step]);
  useEffect(() => {
    api.evt("review_page_opened");
    const abandon = () => {
      if (doneRef.current || !startedRef.current) return;
      doneRef.current = true;
      const cid = localStorage.getItem("welp_cid");
      const blob = new Blob([JSON.stringify({ event: "review_abandoned", meta: { step: stepRef.current }, client_id: cid })], { type: "application/json" });
      navigator.sendBeacon?.(`${API}/events`, blob);
    };
    window.addEventListener("pagehide", abandon);
    return () => { window.removeEventListener("pagehide", abandon); abandon(); };
  }, []);
  const [platforms, setPlatforms] = useState([]);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image (JPG or PNG).");
    if (f.size > 10 * 1024 * 1024) return toast.error("Image must be under 10 MB.");
    setPhotoBusy(true);
    try {
      const b64 = await resizeImageToDataUrl(f, { maxDim: 1400, quality: 0.85 });
      set("photo_b64", b64);
      toast.success("Picture attached");
    } catch {
      toast.error("Couldn't read that image — please try another one.");
    } finally { setPhotoBusy(false); e.target.value = ""; }
  };

  const [d, setD] = useState({
    // step 1
    username: sp.get("u") || "", platform: sp.get("p") || "", recipient_phone: "",
    photo_b64: "",
    // free-text
    initial_attraction: "", change_one_thing: "", positive_moment: "",
    // choice answers
    connection_feel: emptyAnswer(),
    timing: emptyAnswer(),
    age_accurate: emptyAnswer(),
    identity: emptyAnswer(),
    appearance: emptyAnswer(),
    hygiene: emptyAnswer(),
    comm_style: emptyAnswer(),
    exes: emptyAnswer(),
    energy: emptyAnswer(),
    date_again: emptyAnswer(),
    recommend: emptyAnswer(),
    safety: emptyAnswer(),
    // meta
    reviewer_apology: false, attest_true: false, confirmed: false,
  });

  useEffect(() => {
    if (loading) return;
    if (!user) { toast.error("Sign in to submit a review"); nav("/login?next=/review/new"); return; }
    if (user.phone_verified === false) { toast.error("Verify your phone number to submit reviews."); nav("/verify-phone"); return; }
    api.platforms().then(r => setPlatforms(r.platforms)).catch(() => {});
  }, [user, loading, nav]);

  const set = (k, v) => setD(o => ({ ...o, [k]: v }));
  const setAns = (k, patch) => setD(o => ({ ...o, [k]: { ...o[k], ...patch } }));
  const phoneValid = (s) => (s || "").replace(/\D/g, "").length >= 10;

  const total = 10;

  // Per-step validation
  const canNext = () => {
    if (user?.is_admin) return true; // admins can click through every step to verify performance
    if (step === 1) return d.username.trim() && d.platform && d.photo_b64 && phoneValid(d.recipient_phone);
    if (step === 2) return d.initial_attraction.trim() && d.connection_feel.choice;
    if (step === 3) return d.change_one_thing.trim() && d.timing.choice;
    if (step === 4) return d.age_accurate.choice && d.identity.choice;
    if (step === 5) return d.appearance.choice && d.hygiene.choice;
    if (step === 6) return d.comm_style.choice && d.exes.choice;
    if (step === 7) return d.energy.choice && d.positive_moment.trim();
    if (step === 8) return d.date_again.choice && d.recommend.choice;
    if (step === 9) {
      if (!d.safety.choice) return false;
      // Require explanation only if the user picked "Yes, and I'd like to explain."
      if (d.safety.choice === "Yes, and I'd like to explain." && !d.safety.explain.trim()) return false;
      return true;
    }
    if (step === 10) return d.attest_true && d.confirmed;
    return true;
  };

  const submit = async () => {
    if (!window.confirm("This review is PERMANENT. Once submitted it cannot be edited or deleted.\n\nProceed?")) return;
    setBusy(true);
    try {
      const body = {
        username: d.username, platform: d.platform,
        recipient_phone: d.recipient_phone.trim(),
        photo_b64: d.photo_b64,
        initial_attraction: d.initial_attraction.trim(),
        change_one_thing: d.change_one_thing.trim(),
        positive_moment: d.positive_moment.trim(),
        connection_feel: d.connection_feel,
        timing: d.timing,
        age_accurate: d.age_accurate,
        identity: d.identity,
        appearance: d.appearance,
        hygiene: d.hygiene,
        comm_style: d.comm_style,
        exes: d.exes,
        energy: d.energy,
        date_again: d.date_again,
        recommend: d.recommend,
        safety: d.safety,
        reviewer_apology: d.reviewer_apology, confirmed: d.confirmed,
      };
      const r = await api.submitReview(body);
      doneRef.current = true;
      trackFb("Lead");
      toast.success("Review submitted");
      nav(`/profile/${r.profile_id}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 401) { toast.error("Your session expired. Please sign in again."); nav("/login?next=/review/new"); return; }
      if (typeof det === "object" && det !== null) {
        if (det.message) toast.error(det.message + (det.ding ? ` — ${det.ding}` : ""));
        else if (Array.isArray(det)) {
          const first = det[0];
          const field = first?.loc?.slice(-1)?.[0] || "field";
          toast.error(`Please complete "${field}" — ${first?.msg || "invalid"}`);
        } else toast.error("Submission failed. Please complete every step.");
      } else if (typeof det === "string") toast.error(det);
      else toast.error("Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <PageBg poster={POSTER_P5_REVIEW}>
      <Nav />
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="text-veil mb-4">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> Step {step} of {total}
        </div>
        <h1 className="font-heading text-4xl md:text-4xl sm:text-5xl text-[#1A1A1A]">Tell the truth, <span className="italic text-[#FF1A82]">kindly.</span></h1>
        </div>
        <div className="mt-6 h-1.5 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full bg-[#FF1A82] stat-bar transition-all" style={{ width: `${(step / total) * 100}%` }} />
        </div>

        {step === total && (
          <div className="mt-6 rounded-2xl border-2 border-[#10B981] bg-white/70 backdrop-blur-lg p-4 flex items-start gap-3" data-testid="permanent-warning">
            <span className="text-[#10B981] text-2xl leading-none mt-0.5">⚠</span>
            <div className="text-lg text-[#1A1A1A]">
              <strong className="text-[#10B981]">Heads up — this is permanent.</strong> Once you submit, your review <strong>cannot be edited or deleted.</strong> Take your time, be honest, and be respectful.
            </div>
          </div>
        )}

        <div className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-8 md:p-10 space-y-8 animate-fade-up">

          {step === 1 && (
            <>
              <QSection emoji="👤" title="Who are you reviewing?">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Username / Name</label>
                    <input data-testid="wiz-username" value={d.username} onChange={e => set("username", e.target.value)} placeholder="@handle or display name"
                      className="mt-2 w-full bg-white/60 border border-black/10 rounded-2xl px-5 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981]" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Platform</label>
                    <select data-testid="wiz-platform" value={d.platform} onChange={e => set("platform", e.target.value)}
                      className="mt-2 w-full bg-white/60 border border-black/10 rounded-2xl px-5 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981]">
                      <option value="">Select platform</option>
                      {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </QSection>
              <div className="rounded-2xl border-2 border-[#10B981] bg-[#10B981]/10 p-4 space-y-3" data-testid="review-photo-section">
                <label className="text-lg uppercase tracking-[0.12em] text-[#10B981] font-bold">Picture of their dating profile <span className="text-[#5A5A5A] normal-case tracking-normal font-semibold">— required</span></label>
                <p className="text-lg text-[#1A1A1A] font-semibold leading-snug">
                  Upload a <strong>clear, good-quality picture</strong> of the dating profile you're reviewing. A screenshot is totally fine — it just has to be clear. This shows the community your review is about a real profile you actually interacted with.
                </p>
                {d.photo_b64 ? (
                  <div className="space-y-3">
                    <img src={d.photo_b64} alt="Dating profile" className="w-full max-h-[420px] object-contain rounded-2xl border-2 border-[#FF1A82] bg-white" data-testid="review-photo-preview" />
                    <button type="button" onClick={() => photoRef.current?.click()}
                      className="px-5 py-3 rounded-full bg-white/70 border-2 border-[#10B981]/40 text-[#10B981] font-bold text-lg inline-flex items-center gap-2 hover:border-[#10B981]"
                      data-testid="review-photo-change-btn"><Camera className="w-5 h-5" /> Change picture</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => photoRef.current?.click()} disabled={photoBusy}
                    className="w-full py-8 rounded-2xl border-2 border-dashed border-[#10B981]/50 bg-white/60 text-[#10B981] font-bold text-lg inline-flex items-center justify-center gap-3 hover:bg-[#10B981]/5 disabled:opacity-50"
                    data-testid="review-photo-upload-btn">
                    <Camera className="w-6 h-6" /> {photoBusy ? "Reading picture…" : "Upload their profile picture / screenshot"}
                  </button>
                )}
                <input ref={photoRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="review-photo-input" />
              </div>
              <div className="rounded-2xl border-2 border-[#FF1A82] bg-[#FF1A82]/10 p-4 space-y-3">
                <label className="text-lg uppercase tracking-[0.12em] text-[#FF1A82] font-bold">Their phone number <span className="text-[#5A5A5A] normal-case tracking-normal font-semibold">— required</span></label>
                <input data-testid="wiz-recipient-phone" type="tel" value={d.recipient_phone}
                  onChange={e => set("recipient_phone", e.target.value)}
                  placeholder="e.g. 555-123-4567"
                  className="w-full bg-white/70 backdrop-blur-lg border-2 border-black/15 rounded-2xl px-5 py-3 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82]" />
                <p className="text-lg text-[#1A1A1A] font-semibold leading-snug">
                  Welp will text them that a review was posted about them. The text shows only your <strong>Welp username</strong> — never your real name.
                </p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <QSection emoji="👀" title="First..." subtitle="What caught your eye about them?">
                <TextArea label="What caught your eye" value={d.initial_attraction} onChange={v => set("initial_attraction", v)} max={300} testid="wiz-initial-attraction" placeholder="Their smile, their profile, a specific line they wrote…" />
              </QSection>
              <QSection emoji="💫" title="What did that connection feel like?">
                <ChoiceGrid options={CHOICE_CONNECTION_FEEL} value={d.connection_feel.choice} onChange={v => setAns("connection_feel", { choice: v })} testidPrefix="wiz-connection" />
                <ExplainBox value={d.connection_feel.explain} onChange={v => setAns("connection_feel", { explain: v })} testid="wiz-connection-explain" />
              </QSection>
            </>
          )}

          {step === 3 && (
            <>
              <QSection emoji="🔧" title="If you could change 1 thing about this connection, what would it be?">
                <TextArea label="One thing" value={d.change_one_thing} onChange={v => set("change_one_thing", v)} max={600} testid="wiz-change-one" placeholder="Take your time. This is the honest reflection." />
              </QSection>
              <QSection emoji="⏳" title="Give me a feel for the timing.">
                <ChoiceGrid options={CHOICE_TIMING} value={d.timing.choice} onChange={v => setAns("timing", { choice: v })} testidPrefix="wiz-timing" />
                <ExplainBox value={d.timing.explain} onChange={v => setAns("timing", { explain: v })} testid="wiz-timing-explain" />
              </QSection>
            </>
          )}

          {step === 4 && (
            <>
              <QSection emoji="🎂" title="Were they the age they said they were?">
                <ChoiceGrid options={CHOICE_AGE} value={d.age_accurate.choice} onChange={v => setAns("age_accurate", { choice: v })} testidPrefix="wiz-age" />
                <ExplainBox value={d.age_accurate.explain} onChange={v => setAns("age_accurate", { explain: v })} testid="wiz-age-explain" />
              </QSection>
              <QSection emoji="🌟" title="Were they who they said they were?">
                <ChoiceGrid options={CHOICE_IDENTITY} value={d.identity.choice} onChange={v => setAns("identity", { choice: v })} testidPrefix="wiz-identity" />
                <ExplainBox value={d.identity.explain} onChange={v => setAns("identity", { explain: v })} testid="wiz-identity-explain" />
              </QSection>
            </>
          )}

          {step === 5 && (
            <>
              <QSection emoji="✨" title="How did they show up?">
                <ChoiceGrid options={CHOICE_APPEARANCE} value={d.appearance.choice} onChange={v => setAns("appearance", { choice: v })} testidPrefix="wiz-appearance" />
                <ExplainBox value={d.appearance.explain} onChange={v => setAns("appearance", { explain: v })} testid="wiz-appearance-explain" />
              </QSection>
              <QSection emoji="😋" title="Was their hygiene on point?">
                <ChoiceGrid options={CHOICE_HYGIENE} value={d.hygiene.choice} onChange={v => setAns("hygiene", { choice: v })} testidPrefix="wiz-hygiene" />
                <ExplainBox value={d.hygiene.explain} onChange={v => setAns("hygiene", { explain: v })} testid="wiz-hygiene-explain" />
              </QSection>
            </>
          )}

          {step === 6 && (
            <>
              <QSection emoji="💬" title="How was their communication style?">
                <ChoiceGrid options={CHOICE_COMM} value={d.comm_style.choice} onChange={v => setAns("comm_style", { choice: v })} testidPrefix="wiz-comm" />
                <ExplainBox value={d.comm_style.explain} onChange={v => setAns("comm_style", { explain: v })} testid="wiz-comm-explain" />
              </QSection>
              <QSection emoji="💔" title="How often did the ex come up?">
                <ChoiceGrid options={CHOICE_EXES} value={d.exes.choice} onChange={v => setAns("exes", { choice: v })} testidPrefix="wiz-exes" />
                <ExplainBox value={d.exes.explain} onChange={v => setAns("exes", { explain: v })} testid="wiz-exes-explain" />
              </QSection>
            </>
          )}

          {step === 7 && (
            <>
              <QSection emoji="⚡" title="How did their energy feel?">
                <ChoiceGrid options={CHOICE_ENERGY} value={d.energy.choice} onChange={v => setAns("energy", { choice: v })} testidPrefix="wiz-energy" />
                <ExplainBox value={d.energy.explain} onChange={v => setAns("energy", { explain: v })} testid="wiz-energy-explain" />
              </QSection>
              <QSection emoji="⭐" title="Highlight a positive moment.">
                <TextArea label="Positive moment" value={d.positive_moment} onChange={v => set("positive_moment", v)} max={300} testid="wiz-positive-moment" placeholder="The good, no matter how small." />
              </QSection>
            </>
          )}

          {step === 8 && (
            <>
              <QSection emoji="😄" title="Hey man, wanna try again?">
                <ChoiceGrid options={CHOICE_TRY_AGAIN} value={d.date_again.choice} onChange={v => setAns("date_again", { choice: v })} testidPrefix="wiz-tryagain" />
                <ExplainBox value={d.date_again.explain} onChange={v => setAns("date_again", { explain: v })} testid="wiz-tryagain-explain" />
              </QSection>
              <QSection emoji="🤝" title="Would you recommend this person to a friend?">
                <ChoiceGrid options={CHOICE_RECOMMEND} value={d.recommend.choice} onChange={v => setAns("recommend", { choice: v })} testidPrefix="wiz-recommend" />
                <ExplainBox value={d.recommend.explain} onChange={v => setAns("recommend", { explain: v })} testid="wiz-recommend-explain" />
              </QSection>
            </>
          )}

          {step === 9 && (
            <QSection emoji="⚠️" title="Was there anything scary?" subtitle="Please feel confident in using our report option.">
              <ChoiceGrid options={CHOICE_SAFETY} value={d.safety.choice} onChange={v => setAns("safety", { choice: v })} testidPrefix="wiz-safety" />
              {(d.safety.choice === "Yes, and I'd like to explain." || d.safety.choice === "Yes, I'll submit the report.") && (
                <ExplainBox
                  value={d.safety.explain}
                  onChange={v => setAns("safety", { explain: v })}
                  testid="wiz-safety-explain"
                  placeholder={d.safety.choice === "Yes, and I'd like to explain."
                    ? "Please explain what happened (required — up to 300 characters)."
                    : "Anything you want to add to your auto-report (optional)."}
                />
              )}
              {d.safety.choice === "Yes, I'll submit the report." && (
                <div className="mt-2 rounded-xl bg-[#10B981]/10 border-2 border-[#10B981] p-3 text-sm text-white font-semibold">
                  A safety report will be created and sent to Welp admins along with your review.
                </div>
              )}
            </QSection>
          )}

          {step === 10 && (
            <>
              <label className="flex items-start gap-4 cursor-pointer p-6 rounded-2xl border-4 border-[#10B981] bg-white/70 backdrop-blur-lg shadow-lg" data-testid="apology-option">
                <input data-testid="apology" type="checkbox" checked={d.reviewer_apology} onChange={e => set("reviewer_apology", e.target.checked)} className="mt-2 w-7 h-7 accent-[#10B981]" />
                <span className="font-heading text-3xl md:text-4xl font-bold text-[#10B981] leading-snug">I would like to apologize for my part in this experience.</span>
              </label>

              <div className="rounded-2xl border-2 border-[#10B981] bg-white/70 backdrop-blur-lg p-5 space-y-3">
                <div className="text-[#10B981] font-bold uppercase tracking-[0.18em] text-sm flex items-center gap-2">⚠ Final notice — read carefully</div>
                <p className="text-[#1A1A1A] text-base font-semibold">"I am submitting this review with honesty and respect to the best of my knowledge."</p>
                <p className="text-[#1A1A1A] text-base font-semibold">"I am aware that once a review is submitted it can not be changed or deleted."</p>
                <p className="text-[#1A1A1A] text-base font-semibold">"I understand that Welp does not influence reviews in any way and is not responsible."</p>
                <p className="text-[#1A1A1A] text-base font-semibold">"I have read the <Link to="/terms" className="text-[#10B981] underline">terms and conditions</Link>, as well as the <Link to="/privacy" className="text-[#10B981] underline">privacy policies</Link>."</p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl border-2 border-[#FF1A82] bg-[#FF1A82]/10" data-testid="truth-attestation">
                <input data-testid="attest-true" type="checkbox" checked={d.attest_true} onChange={e => set("attest_true", e.target.checked)} className="mt-1 w-5 h-5 accent-[#FF1A82]" />
                <span className="text-[#1A1A1A] font-semibold">I affirm that this review is true and accurate to the best of my knowledge. I understand that Welp is not responsible for the information given or any outcomes that come from it. I have read the terms and conditions.</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="confirm" type="checkbox" checked={d.confirmed} onChange={e => set("confirmed", e.target.checked)} className="mt-1 w-5 h-5 accent-[#10B981]" />
                <span className="text-[#1A1A1A] font-semibold">I confirm submission of this permanent review.</span>
              </label>
            </>
          )}

          <div className="pt-4 flex flex-wrap justify-between items-center gap-3 border-t border-black/10">
            <button type="button" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
              className="px-5 py-3 rounded-full bg-white/70 backdrop-blur-lg border-2 border-black/15 text-[#3A3A3A] font-bold inline-flex items-center gap-2 disabled:opacity-40 hover:border-[#FF1A82]"
              data-testid="wiz-back"><ArrowLeft className="w-4 h-4" /> Back</button>
            {step < total ? (
              <button type="button" onClick={() => canNext() && setStep(s => s + 1)} disabled={!canNext()}
                className="px-7 py-3 rounded-full bg-[#FF1A82] text-white font-bold inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-40"
                data-testid="wiz-next">Continue <ArrowRight className="w-4 h-4" /></button>
            ) : (
              <button type="button" onClick={submit} disabled={busy || !canNext()}
                className="px-7 py-3 rounded-full bg-[#10B981] text-white font-bold inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-40"
                data-testid="wiz-submit"><Check className="w-4 h-4" /> {busy ? "Submitting…" : "Submit review"}</button>
            )}
          </div>
        </div>
      </section>
    </PageBg>
  );
}
